export interface ProgressiveArtworkWrapperLike {
  slotTarget?: number;
  activeArtwork?: number;
  artworks?: unknown[];
}

export interface RankArtworkWrappersForProgressiveLoadInput<TWrapper extends ProgressiveArtworkWrapperLike> {
  wrappers: Array<TWrapper | null | undefined>;
  getSlotVisibility?: (slotTarget: number, wrapper: TWrapper) => boolean;
  getSlotDistance?: (slotTarget: number, wrapper: TWrapper) => number | null | undefined;
  cameraPosition?: unknown;
}

export interface ProgressiveArtworkQueueSummary {
  started: number;
  loaded: number;
  skipped: number;
  stale: number;
  failed: number;
}

export interface ProgressiveArtworkAttachmentResult {
  attached: boolean;
}

export interface RunProgressiveArtworkQueueInput<TWrapper extends ProgressiveArtworkWrapperLike, TPreloadData> {
  rankedWrappers: Array<TWrapper | null | undefined>;
  initialBudget: number;
  backgroundConcurrency: number;
  preloadArtwork: (artworkWrapper: TWrapper) => Promise<TPreloadData | null>;
  loadArtworkInSlot: (
    artworkWrapper: TWrapper,
    slotTarget: number,
    preloadData?: TPreloadData | null
  ) => Promise<ProgressiveArtworkAttachmentResult>;
  isCurrent: () => boolean;
  cleanupPreload?: (preloadData: TPreloadData) => Promise<unknown> | unknown;
  delay?: () => Promise<unknown> | unknown;
  onInitialBudgetComplete?: (summary: ProgressiveArtworkQueueSummary) => void;
}

function hasActiveArtwork(wrapper: ProgressiveArtworkWrapperLike): boolean {
  const activeArtworkIndex = Number(wrapper.activeArtwork);
  return Number.isInteger(activeArtworkIndex) && Boolean(wrapper.artworks?.[activeArtworkIndex]);
}

function hasValidSlotTarget(wrapper: ProgressiveArtworkWrapperLike): wrapper is ProgressiveArtworkWrapperLike & { slotTarget: number } {
  return Number.isInteger(wrapper.slotTarget) && Number(wrapper.slotTarget) >= 0;
}

function normalizeDistance(distance: number | null | undefined): number {
  return Number.isFinite(distance) ? Number(distance) : Number.POSITIVE_INFINITY;
}

export function rankArtworkWrappersForProgressiveLoad<TWrapper extends ProgressiveArtworkWrapperLike>(
  input: RankArtworkWrappersForProgressiveLoadInput<TWrapper>
): TWrapper[] {
  if (!Array.isArray(input.wrappers)) return [];

  return input.wrappers
    .filter((wrapper): wrapper is TWrapper & { slotTarget: number } => {
      return Boolean(wrapper) && hasValidSlotTarget(wrapper) && hasActiveArtwork(wrapper);
    })
    .map((wrapper, originalIndex) => {
      const slotTarget = wrapper.slotTarget;
      return {
        wrapper,
        originalIndex,
        slotTarget,
        visible: Boolean(input.getSlotVisibility?.(slotTarget, wrapper)),
        distance: normalizeDistance(input.getSlotDistance?.(slotTarget, wrapper)),
      };
    })
    .sort((a, b) => {
      if (a.visible !== b.visible) return a.visible ? -1 : 1;
      if (a.distance !== b.distance) return a.distance - b.distance;
      if (a.slotTarget !== b.slotTarget) return a.slotTarget - b.slotTarget;
      return a.originalIndex - b.originalIndex;
    })
    .map((ranked) => ranked.wrapper);
}

function createProgressiveArtworkQueueSummary(): ProgressiveArtworkQueueSummary {
  return {
    started: 0,
    loaded: 0,
    skipped: 0,
    stale: 0,
    failed: 0,
  };
}

function normalizeWholeNumber(value: number, fallback: number): number {
  if (!Number.isFinite(value)) return fallback;
  return Math.max(0, Math.floor(value));
}

async function cleanupStalePreload<TWrapper extends ProgressiveArtworkWrapperLike, TPreloadData>(
  input: RunProgressiveArtworkQueueInput<TWrapper, TPreloadData>,
  preloadData: TPreloadData | null
) {
  if (preloadData === null || preloadData === undefined) return;
  await input.cleanupPreload?.(preloadData);
}

async function runProgressiveArtworkQueueItem<TWrapper extends ProgressiveArtworkWrapperLike, TPreloadData>(
  input: RunProgressiveArtworkQueueInput<TWrapper, TPreloadData>,
  artworkWrapper: TWrapper | null | undefined,
  summary: ProgressiveArtworkQueueSummary
) {
  if (!artworkWrapper || !hasValidSlotTarget(artworkWrapper)) {
    summary.skipped += 1;
    return;
  }

  if (!input.isCurrent()) {
    summary.stale += 1;
    return;
  }

  let preloadData: TPreloadData | null = null;
  summary.started += 1;

  try {
    preloadData = await input.preloadArtwork(artworkWrapper);
  } catch {
    summary.failed += 1;
    return;
  }

  if (!input.isCurrent()) {
    summary.stale += 1;
    try {
      await cleanupStalePreload(input, preloadData);
    } catch {
      summary.failed += 1;
    }
    return;
  }

  try {
    const result = await input.loadArtworkInSlot(
      artworkWrapper,
      artworkWrapper.slotTarget,
      preloadData
    );
    if (result.attached) {
      summary.loaded += 1;
    } else {
      summary.skipped += 1;
    }
  } catch {
    summary.failed += 1;
  }
}

export async function runProgressiveArtworkQueue<TWrapper extends ProgressiveArtworkWrapperLike, TPreloadData>(
  input: RunProgressiveArtworkQueueInput<TWrapper, TPreloadData>
): Promise<ProgressiveArtworkQueueSummary> {
  const summary = createProgressiveArtworkQueueSummary();
  const wrappers = Array.isArray(input.rankedWrappers) ? input.rankedWrappers : [];
  const initialBudget = Math.min(normalizeWholeNumber(input.initialBudget, 0), wrappers.length);
  const backgroundConcurrency = Math.max(1, normalizeWholeNumber(input.backgroundConcurrency, 1));

  for (let index = 0; index < initialBudget; index++) {
    if (!input.isCurrent()) break;
    await runProgressiveArtworkQueueItem(input, wrappers[index], summary);
  }

  if (input.isCurrent()) {
    input.onInitialBudgetComplete?.({ ...summary });
  }

  const backgroundWrappers = wrappers.slice(initialBudget);
  let nextBackgroundIndex = 0;
  const workerCount = Math.min(backgroundConcurrency, backgroundWrappers.length);

  if (!input.isCurrent()) return summary;

  async function runWorker() {
    while (input.isCurrent() && nextBackgroundIndex < backgroundWrappers.length) {
      const artworkWrapper = backgroundWrappers[nextBackgroundIndex];
      nextBackgroundIndex += 1;
      await runProgressiveArtworkQueueItem(input, artworkWrapper, summary);
      if (!input.isCurrent()) return;
      if (nextBackgroundIndex < backgroundWrappers.length) {
        await input.delay?.();
        if (!input.isCurrent()) return;
      }
    }
  }

  await Promise.all(Array.from({ length: workerCount }, () => runWorker()));

  return summary;
}
